import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '@/redux/common/store'
import { voiceAmplitude } from '@/services/voiceAmplitude'
import { cn } from '@/utils/cn'

interface VoiceModeButtonProps {
  onStart: () => Promise<void>
  onStop: () => void
  isMuted?: boolean
  onToggleMute?: () => void
  isSpeakerMuted?: boolean
  onToggleSpeakerMute?: () => void
  showTranscript?: boolean
  onToggleTranscript?: () => void
  /**
   * When provided, a compact 🧪 text-to-SAM input appears inside the chip while voice is active.
   * Intended for QA/test environments only (controlled by onboarding.debug_text_input_enabled).
   */
  onSendText?: (text: string) => void
}

// Shared style for every circular control button (speaker, mic, transcript).
const CONTROL_BTN_CLASS =
  'w-10 h-10 rounded-full flex items-center justify-center transition-transform ' +
  'hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring'

// On = neutral surface chip that reads on any page; muted / hidden = red.
const controlTone = (muted: boolean) =>
  muted ? 'bg-danger-fill text-white' : 'bg-ui-hover text-ui-text hover:bg-ui-border'

export function VoiceModeButton({ onStart, onStop, isMuted = false, onToggleMute, isSpeakerMuted = false, onToggleSpeakerMute, showTranscript = true, onToggleTranscript, onSendText }: VoiceModeButtonProps) {
  // Local state for the inline text-to-SAM input (test only).
  const [textValue, setTextValue] = useState('')
  const textInputRef = useRef<HTMLInputElement>(null)

  const submitText = () => {
    const trimmed = textValue.trim()
    if (!trimmed || !onSendText) return
    onSendText(trimmed)
    setTextValue('')
    textInputRef.current?.focus()
  }
  const { voiceStatus, transcript, transcriptRole, transcriptFinalized, conversationHistory } = useSelector(
    (state: RootState) => state.onboardingVoice,
  )

  const isActive = voiceStatus !== 'idle' && voiceStatus !== 'error'

  // Onboarding voice shows ONLY SAM's current/live message — no transcript history, no user turns.
  const isStreamingAssistant = Boolean(transcript) && !transcriptFinalized && transcriptRole === 'assistant'
  const liveAssistantText = isStreamingAssistant
    ? transcript
    : ([...conversationHistory].reverse().find((t) => t.role === 'assistant')?.text ?? '')
  const hasContent = liveAssistantText.length > 0

  // Auto-scroll as the live message streams.
  const scrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [liveAssistantText])

  // Keep amplitude ref alive (VoiceFAB doesn't use it directly, but voiceAmplitude drives
  // the speaking indicator — subscribing here keeps the rAF loop alive during voice).
  const _ampRef = useRef(voiceAmplitude.get)
  _ampRef.current = voiceAmplitude.get

  const handleClick = async () => {
    if (isActive) {
      onStop()
    } else {
      await onStart()
    }
  }

  // When the test text input is enabled, the chip expands from a pill (rounded-full) to a card
  // (rounded-2xl) with a second row for the input. When inactive both look the same.
  const showTextInput = isActive && !!onSendText

  return (
    <div className="fixed bottom-6 right-6 flex flex-col items-end gap-3 z-50">

      {/* SAM's current/live message only — no transcript history, no user turns. */}
      {isActive && showTranscript && hasContent && (
        <div className="w-72 max-w-[calc(100vw-2rem)] bg-ui-card rounded-card shadow-pop border border-ui-border overflow-hidden">
          <div ref={scrollRef} className="max-h-40 overflow-y-auto px-3 py-3">
            <div className="flex flex-col items-start">
              <span className="text-xs font-medium text-ui-text3 mb-0.5 px-1"></span>
              <div className="max-w-[90%] rounded-2xl px-3 py-2 text-sm leading-snug bg-ui-subtle text-ui-text rounded-bl-sm">
                {liveAssistantText}
                {isStreamingAssistant && (
                  <span className="inline-block w-0.5 h-3 ml-0.5 bg-steel animate-pulse align-middle" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main chip: pill when no text input, card when text input is active.
          Controls row + optional text-to-SAM row sit on a glass surface. */}
      <div className={
        isActive
          ? showTextInput
            ? 'glass rounded-card shadow-pop px-2 pt-1.5 pb-2 flex flex-col gap-1.5'
            : 'glass rounded-full shadow-pop px-2 py-1.5 flex items-center gap-2'
          : 'flex items-center gap-2'
      }>

      {/* Controls row (always inside the chip when active) */}
      <div className={isActive ? 'flex items-center gap-2' : 'flex items-center gap-2'}>
        {/* Transcript show/hide toggle */}
        {isActive && onToggleTranscript && (
          <button
            onClick={onToggleTranscript}
            title={showTranscript ? 'Hide transcript' : 'Show transcript'}
            aria-label={showTranscript ? 'Hide transcript' : 'Show transcript'}
            className={cn(CONTROL_BTN_CLASS, controlTone(!showTranscript))}
          >
            {showTranscript ? (
              /* Eye icon (transcript visible) */
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </svg>
            )}
          </button>
        )}

        {/* Speaker mute toggle */}
        {isActive && onToggleSpeakerMute && (
          <button
            onClick={onToggleSpeakerMute}
            title={isSpeakerMuted ? 'Unmute speaker' : 'Mute speaker'}
            aria-label={isSpeakerMuted ? 'Unmute speaker' : 'Mute speaker'}
            className={cn(CONTROL_BTN_CLASS, controlTone(isSpeakerMuted))}
          >
            {isSpeakerMuted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              /* Speaker icon */
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M15.54 8.46a5 5 0 010 7.07M19.07 4.93a10 10 0 010 14.14" />
              </svg>
            )}
          </button>
        )}

        {/* Mic mute toggle */}
        {isActive && onToggleMute && (
          <button
            onClick={onToggleMute}
            title={isMuted ? 'Unmute mic' : 'Mute mic'}
            aria-label={isMuted ? 'Unmute mic' : 'Mute mic'}
            className={cn(CONTROL_BTN_CLASS, controlTone(isMuted))}
          >
            {isMuted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23M12 19v4M8 23h8" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
                <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
              </svg>
            )}
          </button>
        )}

        {/* Main control — a single labeled toggle. The label always says what a tap does, so
            starting and ending voice is never ambiguous (replaces the tap-the-mascot pattern). */}
        {isActive ? (
          <button
            onClick={handleClick}
            title="End voice"
            aria-label="End voice"
            className="flex items-center gap-2 rounded-full bg-danger-fill px-5 py-3 text-sm font-semibold text-white shadow-pop transition-transform hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger focus-visible:ring-offset-2 focus-visible:ring-offset-ui"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            End voice
          </button>
        ) : (
          <button
            onClick={handleClick}
            title="Talk to SAM"
            aria-label="Talk to SAM"
            className="flex items-center gap-2 rounded-full bg-accent-fill px-5 py-3 text-sm font-semibold text-on-accent shadow-glow-accent transition-transform hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-[18px] h-[18px]">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" />
            </svg>
            Talk to SAM
          </button>
        )}
      </div>{/* end controls row */}

      {/* 🧪 Text-to-SAM row — test/QA only, shown inside the chip when onSendText is wired. */}
      {showTextInput && (
        <div className="flex items-center gap-1.5 bg-warning/10 border border-warning/30 rounded-xl px-2.5 py-1.5 mx-0.5">
          <span className="text-xs font-semibold text-warning-text shrink-0 select-none">🧪</span>
          <input
            ref={textInputRef}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitText(); }
            }}
            placeholder="Type to SAM…"
            className="flex-1 min-w-0 text-xs bg-transparent outline-none text-warning-text placeholder:text-warning-text"
            style={{ width: '160px' }}
          />
          <button
            type="button"
            onClick={submitText}
            disabled={!textValue.trim()}
            aria-label="Send"
            className="w-5 h-5 rounded-full bg-warning-fill flex items-center justify-center shrink-0 transition disabled:opacity-30"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      )}

      </div>{/* end chip */}
    </div>
  )
}
